import {
  keepPreviousData, useMutation, useQueries, useQuery, useQueryClient, UseQueryResult
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import APIGateway from '@/api';
import {
  Evaluation,
  MetricCloneReq,
  MetricConfig,
  MetricCreateReq, MetricGetRes,
  MetricProvider,
  MetricsSearchParams, MetricsSearchRes,
  MetricVersionCreateReq,
  MetricVersionDeprecateReq
} from '@/types/evals';


export const metricsKeys = {
  all: [ 'metrics' ] as const,
  list: () => [ ...metricsKeys.all, 'list' ] as const,
  search: ( params?: MetricsSearchParams ) => [ ...metricsKeys.all, 'search', params ] as const,
  config: ( metricId: string, version: number ) => [ ...metricsKeys.all, 'config', metricId, version ] as const,
  detail: ( metricId: string ) => [ ...metricsKeys.all, 'detail', metricId ] as const
};

const METRICS_STALE_TIME = 5 * 60 * 1000;

export function useMetricQuery( metricProviderId: string | undefined ) {
  return useQuery<MetricGetRes>( {
    queryKey: metricsKeys.detail( metricProviderId ?? '' ),
    queryFn: async() => {
      if ( !metricProviderId ) throw new Error( 'Metric provider id required' );
      return await APIGateway.evals.metricGet( metricProviderId );
    },
    enabled: !!metricProviderId
  } );
}

export function useCreateMetricVersionMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: { metricId: string; data: MetricVersionCreateReq } ) => {
      return await APIGateway.evals.metricVersionCreate( args.metricId, args.data );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: metricsKeys.detail( variables.metricId ) } );
      queryClient.invalidateQueries( { queryKey: metricsKeys.all } );
    }
  } );
}

export function useCloneMetricMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: { metricId: string; version: string; data: MetricCloneReq } ) => {
      return await APIGateway.evals.metricClone( args.metricId, args.version, args.data );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: metricsKeys.all } );
    }
  } );
}

export function useDeprecateMetricVersionMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: { metricId: string; version: number; data: MetricVersionDeprecateReq } ) => {
      return await APIGateway.evals.metricVersionDeprecate( args.metricId, args.version, args.data );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: metricsKeys.config( variables.metricId, variables.version ) } );
      queryClient.invalidateQueries( { queryKey: metricsKeys.detail( variables.metricId ) } );
    }
  } );
}


export function useMetricsQuery() {
  return useQuery( {
    queryKey: metricsKeys.list(),
    queryFn: async() => {
      const metrics = await APIGateway.evals.metricsGet();

      return metrics;
    }
  } );
}

export function useMetricConfigQuery(
  metricId: string | undefined,
  version: number | undefined
) {
  return useQuery( {
    queryKey: metricsKeys.config( metricId ?? '', version ?? 0 ),
    queryFn: async() => {
      if ( !metricId || version === undefined ) throw new Error( 'Metric ID and version required' );
      const config = await APIGateway.evals.metricConfigGet( metricId, version );
      return config;
    },
    enabled: !!metricId && version !== undefined,
    staleTime: METRICS_STALE_TIME
  } );
}

export function useMetricsSearch( params: MetricsSearchParams ) {
  return useQuery<{ items: MetricConfig[]; hasMore: boolean; nextCursor: string | null }>( {
    refetchOnWindowFocus: false,
    queryKey: metricsKeys.search( params ),
    queryFn: async() => {
      const res: MetricsSearchRes = await APIGateway.evals.metricsSearch( params );
      return { items: res.items, hasMore: res.pagination.hasMore, nextCursor: res.pagination.nextCursor };
    },
    placeholderData: keepPreviousData
  } );
}

export type EvaluationMetricRow = {
  metricProviderId: string;
  name: string | null;
  version: number | null;
  isDeprecated: boolean;
  createdAt: string | null;
};

/**
 * Resolves the metrics pinned to an evaluation into displayable rows.
 *
 * The evaluation only stores (metricProviderId, versionId) pairs, so each
 * provider is fetched to resolve its name and version number, then the pinned
 * version's config is fetched for deprecation status and creation date. An
 * individual fetch failure degrades that row's fields rather than failing the
 * whole set.
 */
export function useEvaluationMetrics( metricProviders: Evaluation['metricProviders'] ) {
  const providerIds = useMemo(
    () => [ ...new Set( metricProviders.map( mp => mp.metricProviderId ) ) ],
    [ metricProviders ]
  );

  // Both `combine` callbacks below MUST be referentially stable. An inline
  // closure makes TanStack recompute the combined value on every snapshot
  // read, and a fresh Map can never be structurally shared, so React's
  // useSyncExternalStore sees a new snapshot each check and re-renders in a
  // tight synchronous loop — freezing the tab (e.g. when the run-create modal
  // mounted while this hook was subscribed).
  const combineProviders = useCallback( ( results: UseQueryResult<MetricProvider>[] ) => ( {
    byId: new Map<string, MetricProvider>(
      results.flatMap( ( r, i ) => ( r.data ? [ [ providerIds[ i ], r.data ] as const ] : [] ) )
    ),
    isLoading: results.some( r => r.isLoading ),
    allFailed: results.length > 0 && results.every( r => r.isError )
  } ), [ providerIds ] );

  const providers = useQueries( {
    queries: providerIds.map( id => ( {
      queryKey: metricsKeys.detail( id ),
      queryFn: () => APIGateway.evals.metricGet( id ),
      staleTime: METRICS_STALE_TIME
    } ) ),
    combine: combineProviders
  } );

  const resolved = useMemo( () => metricProviders.map( mp => {
    const provider = providers.byId.get( mp.metricProviderId );
    const versions = provider?.versions ?? [];
    const version = mp.versionId
      ? versions.find( v => v.id === mp.versionId )?.version ?? null
      : versions.reduce<number | null>( ( max, v ) => Math.max( max ?? 0, v.version ), null );
    return { metricProviderId: mp.metricProviderId, name: provider?.name ?? null, version };
  } ), [ metricProviders, providers.byId ] );

  const configTargets = useMemo(
    () => resolved.flatMap( r => ( r.version != null ? [ { metricProviderId: r.metricProviderId, version: r.version } ] : [] ) ),
    [ resolved ]
  );

  const combineConfigs = useCallback( ( results: UseQueryResult<MetricConfig>[] ) => ( {
    byKey: new Map<string, MetricConfig>(
      results.flatMap( ( r, i ) => (
        r.data ? [ [ `${ configTargets[ i ].metricProviderId }|${ configTargets[ i ].version }`, r.data ] as const ] : []
      ) )
    ),
    isLoading: results.some( r => r.isLoading )
  } ), [ configTargets ] );

  const configs = useQueries( {
    queries: configTargets.map( t => ( {
      queryKey: metricsKeys.config( t.metricProviderId, t.version ),
      queryFn: () => APIGateway.evals.metricConfigGet( t.metricProviderId, t.version ),
      staleTime: METRICS_STALE_TIME
    } ) ),
    combine: combineConfigs
  } );

  const rows = useMemo<EvaluationMetricRow[]>( () => resolved.map( r => {
    const config = r.version != null ? configs.byKey.get( `${ r.metricProviderId }|${ r.version }` ) : undefined;
    return {
      metricProviderId: r.metricProviderId,
      name: r.name,
      version: r.version,
      isDeprecated: config?.isDeprecated ?? false,
      createdAt: config?.createdAt ?? null
    };
  } ), [ resolved, configs.byKey ] );

  return {
    rows,
    isLoading: providers.isLoading || configs.isLoading,
    isError: providers.allFailed
  };
}

export function useCreateMetricMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: MetricCreateReq ) => {
      const result = await APIGateway.evals.metricCreate( args );

      return result;
    },
    onSuccess: () => {
      // Invalidate and refetch metrics list
      queryClient.invalidateQueries( { queryKey: metricsKeys.all } );
    }
  } );
}
